/**
 * Layout/background selection — frontend port of platform's
 * domain/algo/layout/select_layout.py.
 *
 * Given the symbols currently composing the tapestry, choose a curated
 * background (and its layout). One deliberate difference from the platform
 * algorithm: a deterministic fallback (no RNG) so the selection is reproducible
 * when scrubbing the playhead — the whole render pipeline is a pure function of
 * (segments, currentSent), and selection must not break that.
 *
 * Selection itself is stateless; hysteresis (committing to a choice only once
 * it's stable) lives in the reduceTapestry replay in useTapestryState.
 */

/**
 * Whether `symbolId` is in the curated primary-symbol whitelist `primarySet`
 * (a Set carried on the prepared catalog). Only whitelisted symbols may become
 * the rendered primary and drive background selection; every other symbol still
 * appears as a secondary/tertiary foreground symbol, it just can't claim the
 * center or pick the backdrop (the "background feels unrelated" effect).
 *
 * When the set is missing/empty (e.g. the host's adapter predates the whitelist,
 * or the service returned nothing) we don't gate — every symbol is eligible,
 * preserving pre-whitelist behavior.
 */
export function isPrimaryCandidate(symbolId, primarySet) {
  return !primarySet || primarySet.size === 0 || primarySet.has(symbolId);
}

/**
 * Build the eligible-background catalog once from the raw adapter data. Mirrors
 * the backend's filter: only `published` backgrounds belonging to an `active`
 * layout that actually has slots are eligible — a background on a slotless
 * layout would commit but render nothing (empty composition), bouncing the view
 * back to the generative layout. If no layouts are supplied we skip the layout
 * filter rather than excluding everything.
 *
 * `published` is the one flag (TT-968; `selected` is gone from the catalog).
 * The gate mirrors the backend (resources.py filters `{published: True}`), but
 * the tapsrv `/tap/backgrounds` feed returns ALL backgrounds unfiltered, so
 * unpublished backgrounds would otherwise be selectable here. We exclude only
 * backgrounds EXPLICITLY marked unpublished (`published === false`); an absent
 * field is treated as published so adapters whose source already filters
 * server-side (e.g. appback, which omits the field) keep their catalog.
 *
 * `primarySymbolIds` is the curated whitelist of symbol_ids allowed to drive the
 * primary role + background selection (from tapsrv /tap/symbols/primary). It's
 * stored as a Set on the returned catalog so it flows to reduceTapestry and
 * selectLayout alongside the backgrounds. Empty ⇒ no gating.
 */
export function prepareLayoutCatalog(backgrounds = [], layouts = [], primarySymbolIds = []) {
  const renderableLayoutIds = new Set(
    (layouts || []).filter((l) => l && l.active && Array.isArray(l.slots) && l.slots.length > 0).map((l) => l.id)
  );
  const filterByLayout = renderableLayoutIds.size > 0;
  const eligible = (backgrounds || []).filter(
    (b) =>
      b &&
      b.published !== false &&
      b.id &&
      b.layoutId &&
      (!filterByLayout || renderableLayoutIds.has(b.layoutId))
  );
  // Sort by id once so the seeded no-match default (pickDefault) indexes a stable
  // order without re-sorting the whole catalog on every replay step.
  eligible.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return { backgrounds: eligible, primarySymbolIds: new Set(primarySymbolIds || []) };
}

// How many of this background's associated symbols are present in the tapestry.
function relevance(bg, counts) {
  let n = 0;
  for (const symbolId of bg.symbolIds || []) if (counts.has(symbolId)) n++;
  return n;
}

function styleOf(bg) {
  return (bg.styles && bg.styles[0]) || null;
}

/**
 * Choose the best among matched candidates: most thematically relevant, then a
 * stable id tiebreak (keeps the result deterministic). All matched candidates
 * already share the key matching symbol, so this just ranks by topic relevance.
 *
 * Progressive-piece backgrounds (TT-1004): a piece background reveals over time
 * rather than as one image, so between two backgrounds that fit equally well the
 * pieced one is the better render. Where it sits in the ordering depends on how
 * the match was made:
 *
 *   - tier 1 (`primaryMatch`) — every candidate's OWN primary_symbol_id is the
 *     matched symbol, so they are all genuinely about it and thematic fit is
 *     already settled; `symbolIds[]` relevance is only a refinement. Pieces rank
 *     ABOVE it. This is what the ordering has to be to matter at all: the piece
 *     backgrounds authored so far carry an empty `symbolIds[]`, so their relevance
 *     is always 0 and a tiebreak below relevance would never fire.
 *   - tier 2 — the match is weak (the symbol is merely listed in `symbolIds[]`),
 *     so relevance is carrying the theme and must stay first; pieces only break
 *     ties under it.
 */
function pickBest(candidates, counts, primaryMatch = false) {
  const pieces = (b) => (b.hasPieces ? 1 : 0);
  return candidates.slice().sort((a, b) => {
    if (primaryMatch && pieces(b) !== pieces(a)) return pieces(b) - pieces(a);
    const ra = relevance(a, counts);
    const rb = relevance(b, counts);
    if (rb !== ra) return rb - ra;
    if (pieces(b) !== pieces(a)) return pieces(b) - pieces(a);
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  })[0];
}

// FNV-1a 32-bit string hash — stable across runs/platforms (no RNG), so a given
// seed always maps to the same background and scrubbing stays reproducible.
function hashString(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * No-symbol-match fallback: a per-meeting default. Indexes the (id-sorted)
 * catalog by a hash of the seed, so different meetings get different defaults
 * while the same meeting stays consistent. An empty seed degrades to a single
 * fixed default.
 */
function pickDefault(bgs, seed) {
  if (!bgs.length) return null;
  return bgs[hashString(String(seed ?? '')) % bgs.length];
}

/**
 * @param {Map<string, number>} counts  symbol_id -> count
 * @param {{backgrounds: Array}} catalog from prepareLayoutCatalog
 * @param {string|null} seed  stable per-meeting key; seeds the no-match default
 * @returns {{backgroundId: string, layoutId: string, style: string|null, matchedSymbolId: string|null, primaryMatch: boolean}|null}
 */
export function selectLayout(counts, catalog, seed = null) {
  const bgs = catalog && catalog.backgrounds;
  if (!bgs || bgs.length === 0) return null;
  if (!counts || counts.size === 0) return null;

  // Only whitelisted symbols may drive selection. If none are present, leave the
  // background uncommitted (the host keeps rendering the generative packer)
  // rather than forcing an unrelated backdrop off a non-curated symbol.
  const primarySet = catalog.primarySymbolIds;
  const sorted = [...counts.entries()]
    .filter(([symbolId]) => isPrimaryCandidate(symbolId, primarySet))
    .sort((a, b) => b[1] - a[1])
    .map(([symbolId]) => symbolId);
  if (!sorted.length) return null;

  let matched = [];
  let matchedSymbolId = null; // symbol that drove the match; null on the no-match default
  let primaryMatch = false; // true only when matchedSymbolId is the chosen bg's primary symbol

  // 1) a background whose primary_symbol_id matches one of the top-2 symbols
  for (const symbolId of sorted.slice(0, 2)) {
    matched = bgs.filter((b) => b.primarySymbolId === symbolId);
    if (matched.length) {
      matchedSymbolId = symbolId;
      primaryMatch = true;
      break;
    }
  }

  // 2) else the largest symbol that appears in any background's symbol_ids
  if (!matched.length) {
    for (const symbolId of sorted) {
      matched = bgs.filter((b) => Array.isArray(b.symbolIds) && b.symbolIds.includes(symbolId));
      if (matched.length) {
        matchedSymbolId = symbolId;
        break;
      }
    }
  }

  // 3) no symbol match -> a per-meeting seeded default (deterministic, but
  //    varied across meetings) instead of always the same lowest-id background.
  const best = matched.length ? pickBest(matched, counts, primaryMatch) : pickDefault(bgs, seed);
  if (!best) return null;
  return { backgroundId: best.id, layoutId: best.layoutId, style: styleOf(best), matchedSymbolId, primaryMatch };
}
